'use client'

import * as React from 'react'
import { CheckCircle2, Loader2 } from 'lucide-react'

// Inline email gate for a playbook card. Full Name + Work Email + Company
// → POST /api/playbooks/gate → autoresponder + internal alert. On success
// shows a "link sent" confirmation in place of the form.

// Optional first-order incentive shown under the CTA (Whatmore-style
// "discount + trial" line). Empty string = hidden, same pattern as
// pagespeed-proof's SCREENSHOT_SRC. Set it only when there is a real,
// approved offer, e.g. 'Playbook readers get their first month free.'
const FIRST_ORDER_INCENTIVE = ''

export function PlaybookGate({
  slug,
  compact = false,
}: {
  slug: string
  compact?: boolean
}) {
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = React.useState('')

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus('loading')
    const fd = new FormData(e.currentTarget)
    const payload = {
      email:   String(fd.get('email')   ?? '').trim(),
      name:    String(fd.get('name')    ?? '').trim(),
      company: String(fd.get('company') ?? '').trim(),
      slug,
      url: String(fd.get('url') ?? ''), // honeypot
    }
    try {
      const res = await fetch('/api/playbooks/gate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = (await res.json()) as { ok: boolean; error?: string }
      if (!res.ok || !json.ok) throw new Error(json.error ?? 'Request failed')
      setStatus('done')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Try again.')
      setStatus('error')
    }
  }

  if (status === 'done') {
    return (
      <div className="flex flex-col items-center gap-2 py-4 text-center">
        <CheckCircle2 className="h-8 w-8 text-primary" />
        <p className="font-display text-base font-semibold">Playbook on its way.</p>
        <p className="text-sm text-muted-foreground">Check your inbox — we've emailed you the link.</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={compact ? 'space-y-2' : 'space-y-3'} noValidate>
      {/* Honeypot — hidden from real users, traps bots that fill all fields */}
      <input type="text" name="url" tabIndex={-1} aria-hidden className="hidden" />

      {!compact && (
        <input
          type="text"
          name="name"
          placeholder="Full name"
          autoComplete="name"
          className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      )}

      <input
        type="email"
        name="email"
        placeholder="Work email"
        required
        autoComplete="email"
        className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
      />

      {!compact && (
        <input
          type="text"
          name="company"
          placeholder="Company (optional)"
          autoComplete="organization"
          className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      )}

      {status === 'error' && (
        <p className="text-xs text-destructive">{errorMsg}</p>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
      >
        {status === 'loading' ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending…
          </>
        ) : (
          'Get the playbook'
        )}
      </button>

      {FIRST_ORDER_INCENTIVE ? (
        <p className="text-center text-[11px] font-medium text-primary">{FIRST_ORDER_INCENTIVE}</p>
      ) : null}
      <p className="text-center text-[11px] text-muted-foreground">
        No sequences. One email, the link, done.
      </p>
    </form>
  )
}
